/**
 * The per-row result shape, as `GET /jobs/{id}/result` now returns it.
 *
 * Mirrors `xplatform_mapping_service/src/api_service/models/` — `base.py` plus
 * `response/tm/` and `response/oe/`. The old shape was service-agnostic (one
 * `text`, one open `context` dict, one `top_match` whose `metadata` keys were
 * "whatever the plugin indexed"), so every consumer had to know which plugin
 * wrote a row before it could read it. This one splits along the axis that
 * actually differs:
 *
 *  - what EVERY row has — the index, the two orthogonal statuses, whether
 *    candidates / examples exist, the reviewer's pick;
 *  - what a SERVICE has — a `source` (the input as that service describes it)
 *    and a `target` (the catalogue entry it resolved to).
 *
 * `kind` is the discriminator, and it is on the ROW because a row travels
 * alone: the PATCH and add-row routes return one with no envelope around it.
 * It is deliberately NOT on a target — a target is always nested in something
 * that already says what shape it is.
 *
 * Wire types are snake_case; ViewModels are the camelCase shapes the UI reads.
 * Convert at the boundary with the builders in
 * `src/utils/builders/mappingResult.ts`.
 */

import type { JobStatus, DisplayConfig, JobError, JobErrorViewModel } from "@models/job";
import type { MappingStatus, ReviewStatus } from "@models/job";
import type { ScoreBasis } from "@models/search";

/**
 * Which service's `source` / `target` shapes a row carries.
 *
 * A named set rather than an inline `"tm" | "oe"` so every place that branches
 * on it — the row union, the alternatives fetch, the table variant — is
 * provably talking about the same closed vocabulary, and refers to a member
 * rather than retyping a string. Adding a third service means one edit here
 * and a compile error at every site that has to decide.
 */
export const MappingResultKind = {
  Tm: "tm",
  Oe: "oe",
} as const;
export type MappingResultKind =
  (typeof MappingResultKind)[keyof typeof MappingResultKind];

/**
 * The measurements an OE match can report, keyed by what each one measures.
 *
 * Closed to what the backend actually emits (`_SCORE_KEYS` +
 * `_SCORE_METADATA_KEYS` in `oe/rows.py`) rather than an open
 * `Record<string, number>`: a typo in a key should not compile, and a
 * catalogue-miss proposal reports only `LlmConfidence` — the retrieval numbers
 * are absent on it by design, which `Partial` states and an open record hides.
 */
export const OeScoreKey = {
  Similarity: "similarity",
  Distance: "distance",
  LlmConfidence: "llm_confidence",
} as const;
export type OeScoreKey = (typeof OeScoreKey)[keyof typeof OeScoreKey];

/** How sure the select step is about a match it chose. OE only. */
export const ConfidenceBand = {
  High: "high",
  Medium: "medium",
  Low: "low",
} as const;
export type ConfidenceBand =
  (typeof ConfidenceBand)[keyof typeof ConfidenceBand];

// ---------- Wire types (snake_case, server-shaped) ---------------------

/** Sparse named TM vehicle fields; the API omits facts absent from a manual. */
export interface TmVariantFields {
  manufacturer?: string;
  model?: string;
  series_code?: string;
  model_year?: string;
  model_code?: string;
  engine_code?: string;
  displacement_cc?: string;
  fuel?: string;
  drive?: string;
  body?: string;
  power_kw?: string;
  power_hp?: string;
  cylinders?: string;
  valves?: string;
  engine_type?: string;
  fuel_mixture?: string;
  vehicle_type?: string;
  vehicle_year_from?: string;
  vehicle_year_to?: string;
  /** The months the document's file name prints, when it prints them. */
  vehicle_month_from?: string;
  vehicle_month_to?: string;
  /** Preserved when manually selecting a historical 19-column variant. */
  year_from?: string;
  year_to?: string;
}

export type TmVariant = TmVariantFields | string;

/** The technical-manual input, as extracted from the manual. */
export interface TmSourceWire {
  /** Joined by the API from the FILE# origin, not stored on the row. */
  process_item_id?: string | null;
  /** Joined FILE# display name, not stored on the row. */
  source_filename?: string | null;
  /** The normalised value — the exact string that was embedded. */
  cleaned_text: string | null;
  /** Where in the document it was read from (breadcrumb). */
  path: string | null;
  /** The value exactly as extracted, before cleaning. */
  raw_text: string | null;
  /** Vehicle variant/trim the value was extracted under. */
  variant: TmVariant | null;
  /** Record-level details (e.g. gearbox), distinct from the composite variant. */
  variant_qualifiers?: Record<string, string> | null;
  /** QA-template category of the pinned field. Not stored on the row —
   *  resolved per request, so it is `null` when the plugin has no template. */
  category: string | null;
  /** Source maintenance condition shown beneath the extracted value. */
  remark: string | null;
  /** Controlled market/country codes in ISO alpha-2 format, sorted by the API. */
  country_codes: string | null;
}

/**
 * One technical-manual catalogue value — the row's match, or a candidate for
 * it. The same shape serves both: `rank` is what tells them apart, and what a
 * pick is made with.
 */
export interface TmTargetWire {
  rank: number | null;
  key: string | null;
  value_display: string | null;
  item_mp_id: number | null;
  item_mp_listing: string | null;
  unit: string | null;
  /** 0-100. `null` when nothing was measured — render nothing rather than a
   *  substitute; `score_basis` says what the number is evidence of. */
  score: number | null;
  /** What `score` is evidence of. The vocabulary is the search service's
   *  (`ScoreBasis`), passed through unchanged. */
  score_basis: ScoreBasis | null;
  /** The value carries the queried number in a compatible unit, decided
   *  arithmetically over the search service's whole pool — not by cosine. */
  exact_value_match: boolean | null;
  /** A numeric disagreement made this exact catalogue hit require review. */
  digit_check_failed?: boolean;
}

/** The OE input — one original-equipment part as the supplier names it. */
export interface OeSourceWire {
  oe_number: string | null;
  oe_name: string | null;
  oe_function: string | null;
  oe_domain: string | null;
}

/** What answered a row. See `OeTargetWire.decided_by`. */
export const DecidedBy = {
  Alias: "alias",
  Catalogue: "catalogue",
  Llm: "llm",
} as const;
export type DecidedBy = (typeof DecidedBy)[keyof typeof DecidedBy];

/** Who wrote a proposal's text. See `OeTargetWire.suggested_text_source`. */
export const SuggestedTextSource = {
  Llm: "llm",
  SourceText: "source_text",
} as const;
export type SuggestedTextSource =
  (typeof SuggestedTextSource)[keyof typeof SuggestedTextSource];

/** One catalogue component — the row's match or a candidate for it.
 * A WD `no_match` can carry a proposed name without a catalogue id. */
export interface OeTargetWire {
  rank: number | null;
  itemmp_id: number | null;
  itemmp_text: string | null;
  /** The measurements behind `score`. A proposal reports only
   *  `llm_confidence` — it was not measured against anything. */
  scores: Partial<Record<OeScoreKey, number>>;
  main_group: string | null;
  sub_group: string | null;
  score: number | null;
  /** The name proposed for a catalogue entry that does not exist yet. Never a
   *  stored record — read `suggested_text_source` before calling it the AI's. */
  suggested_text: string | null;
  /** WHO wrote `suggested_text`. `llm` when the naming step proposed it,
   *  `source_text` when that step named nothing and the part's own OE name
   *  stands in so the row is still actionable. `null` on anything that is not
   *  a proposal.
   *
   *  Absent means `llm`: rows decided before the backend stamped this were all
   *  model-written, and reading a missing value as the fallback would blank the
   *  badge on every one of them. */
  suggested_text_source: SuggestedTextSource | null;
  /** What answered this row — `alias` (a mapping a reviewer confirmed before,
   *  replayed), `catalogue` (the sheet spelled the catalogue's own name for the
   *  part) or `llm` (the select call chose it). `null` on a proposal, which
   *  nothing chose.
   *
   *  Only the third is a model's judgement, which is why the confidence band is
   *  read together with this: a band shown on a human's answer would report a
   *  doubt nobody has. */
  decided_by: DecidedBy | null;
  reason: string | null;
  /** Confidence of this candidate, independently of the row's shared
   * machine verdict; WD may have low confidence even on a `no_match`. */
  confidence_band: ConfidenceBand | null;
}

interface MappingResultRowBaseWire {
  row_index: number;
  file_index: number;
  reason?: string | null;
  review_status: ReviewStatus;
  mapping_status: MappingStatus;
  has_examples: boolean;
  /** True when this row has candidates besides the one `target` points at.
   *  A property of the ROW — fetch the candidates from
   *  `GET .../results/{row_index}/alternatives`. */
  has_alternative: boolean;
  error: JobError | null;
  /** The rank the reviewer picked, once they pick one. `null` until then —
   *  `target` is the pipeline's own top, at `target.rank` 1. */
  chosen_rank: number | null;
}

export interface MappingResultRowTmWire extends MappingResultRowBaseWire {
  kind: typeof MappingResultKind.Tm;
  source: TmSourceWire;
  /** `null` when there is nothing to resolve to — see `mapping_status`. */
  target: TmTargetWire | null;
}

export interface MappingResultRowOeWire extends MappingResultRowBaseWire {
  kind: typeof MappingResultKind.Oe;
  source: OeSourceWire;
  target: OeTargetWire | null;
}

export type MappingResultRowWire =
  | MappingResultRowTmWire
  | MappingResultRowOeWire;

export interface MappingResultResponseWire {
  job_id: string;
  status: JobStatus;
  /** Fixes which row shape `rows` carries. */
  plugin_id: string;
  rows: MappingResultRowWire[];
  cursor: string | null;
  display: DisplayConfig | null;
}

/**
 * Body of `GET /jobs/{id}/results/{row_index}/alternatives`.
 *
 * No `kind` here, unlike a row: one request is one row, whose kind the caller
 * already had in hand when it opened the dropdown. The two target shapes have
 * disjoint field names, so they parse unambiguously either way — but the
 * builder takes the kind explicitly rather than sniffing fields.
 */
export interface RowAlternativesResponseWire {
  job_id: string;
  row_index: number;
  /** Rank 1 first — the row's ORIGINAL top match, kept in the list so a
   *  correction stays undoable. */
  alternatives: (TmTargetWire | OeTargetWire)[];
}

// ---------- ViewModel types (camelCase, UI-shaped) ---------------------

export interface TmSource {
  sourceFilename: string | null;
  cleanedText: string | null;
  path: string | null;
  rawText: string | null;
  variant: TmVariant | null;
  variantQualifiers: Record<string, string> | null;
  category: string | null;
  remark: string | null;
  countryCodes: string | null;
}

export interface TmTarget {
  rank: number | null;
  key: string | null;
  valueDisplay: string | null;
  itemMpId: number | null;
  itemMpListing: string | null;
  unit: string | null;
  score: number | null;
  scoreBasis: ScoreBasis | null;
  exactValueMatch: boolean | null;
  digitCheckFailed: boolean;
}

export interface OeSource {
  oeNumber: string | null;
  oeName: string | null;
  oeFunction: string | null;
  oeDomain: string | null;
}

export interface OeTarget {
  rank: number | null;
  itemMpId: number | null;
  itemMpText: string | null;
  scores: Partial<Record<OeScoreKey, number>>;
  mainGroup: string | null;
  subGroup: string | null;
  score: number | null;
  suggestedText: string | null;
  reason: string | null;
  /** See `OeTargetWire.confidence_band`. */
  confidenceBand: ConfidenceBand | null;
  /** See `OeTargetWire.suggested_text_source`. */
  suggestedTextSource: SuggestedTextSource | null;
  /** See `OeTargetWire.decided_by`. */
  decidedBy: DecidedBy | null;
  /** A proposed catalogue entry, not an existing target. The machine verdict
   * stays `no_match` even after a reviewer chooses a real candidate. */
  isProposal: boolean;
  /** True when the MODEL wrote that proposal, which is the only case that may
   *  be shown as an AI suggestion.
   *
   *  A proposal is not always one. When the naming step returns nothing for a
   *  row the backend falls back to the part's own OE name, so the reviewer has
   *  something to act on — and badging the customer's own words as the AI's
   *  proposal would credit the model with a name it declined to write, in the
   *  one place the reviewer is least able to check. */
  isAiSuggestion: boolean;
  /** A model's high or medium confidence; low is shown as a finding badge. */
  aiConfidence: Exclude<ConfidenceBand, "low"> | null;
}

interface MappingResultRowBase {
  rowIndex: number;
  fileIndex: number;
  reason: string | null;
  reviewStatus: ReviewStatus;
  mappingStatus: MappingStatus;
  hasExamples: boolean;
  hasAlternative: boolean;
  error: JobErrorViewModel | null;
  chosenRank: number | null;
}

export interface MappingResultRowTm extends MappingResultRowBase {
  kind: typeof MappingResultKind.Tm;
  source: TmSource;
  target: TmTarget | null;
}

export interface MappingResultRowOe extends MappingResultRowBase {
  kind: typeof MappingResultKind.Oe;
  source: OeSource;
  target: OeTarget | null;
}

/** Narrow on `kind` before reading `source` / `target`. */
export type MappingResultRow = MappingResultRowTm | MappingResultRowOe;

export interface MappingResult {
  jobId: string;
  status: JobStatus;
  pluginId: string;
  rows: MappingResultRow[];
  cursor: string | null;
  display: DisplayConfig | null;
}

/** Any catalogue entry a row points at — or could point at. */
export type MappingResultTarget = TmTarget | OeTarget;

export type RowAlternatives =
  | {
      kind: typeof MappingResultKind.Tm;
      jobId: string;
      rowIndex: number;
      alternatives: TmTarget[];
    }
  | {
      kind: typeof MappingResultKind.Oe;
      jobId: string;
      rowIndex: number;
      alternatives: OeTarget[];
    };

// ---------- Guards -----------------------------------------------------

export const isTmRow = (row: MappingResultRow): row is MappingResultRowTm =>
  row.kind === MappingResultKind.Tm;

export const isOeRow = (row: MappingResultRow): row is MappingResultRowOe =>
  row.kind === MappingResultKind.Oe;
